import { AddCircleOutline, Close as CloseIcon } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  TextField,
  Typography,
  DialogContent,
} from "@mui/material";
import { useEffect, useState } from "react";
import AppButton from "../../../../base/AppButton.component";
import Theme from "../../../../../utils/Theme";
import { SalesAidsStyles } from "./SalesAidsStyles";
import { makeExclusionColumn } from "./warrantyConstants";

/**
 * TIME letter popup — uncovered items table editor.
 * Title / Footer on the exclusion; each column = header + bullet list.
 */
const TimeExclusionPopup = ({ letter, initialItem, onSave }) => {
  const [meta, setMeta] = useState(() => ({
    title_en: initialItem?.title_en || "",
    title_es: initialItem?.title_es || "",
    footer_en: initialItem?.footer_en || "",
    footer_es: initialItem?.footer_es || "",
  }));

  const [columns, setColumns] = useState(() => {
    if (initialItem?.columns?.length) {
      return initialItem.columns.map((col) => ({
        ...makeExclusionColumn(),
        ...col,
        id: col.id || makeExclusionColumn().id,
        column_id: col.column_id ?? null,
      }));
    }
    return [makeExclusionColumn()];
  });

  useEffect(() => {
    onSave?.({
      ...meta,
      // Keep legacy fields in sync so old consumers still work
      what_to_know_text_en: meta.title_en,
      what_to_know_text_es: meta.title_es,
      description_text_en: meta.footer_en,
      description_text_es: meta.footer_es,
      exclusion_category_en: meta.title_en,
      exclusion_category_es: meta.title_es,
      exclusion_items_en: meta.footer_en,
      exclusion_items_es: meta.footer_es,
      columns,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns, meta]);

  const handleMetaChange = (field, value) => {
    setMeta((prev) => ({ ...prev, [field]: value }));
  };

  const handleColumnChange = (id, field, value) => {
    setColumns((prev) =>
      prev.map((col) => (col.id === id ? { ...col, [field]: value } : col))
    );
  };

  const handleAddColumn = () => {
    setColumns((prev) => [...prev, makeExclusionColumn()]);
  };

  const handleRemoveColumn = (id) => {
    setColumns((prev) =>
      prev.length <= 1 ? prev : prev.filter((col) => col.id !== id)
    );
  };

  return (
    <DialogContent>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
          Uncovered Items (TIME — {letter})
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography sx={{ ...SalesAidsStyles.wordStyle, flex: 1 }}>
            English
          </Typography>
          <Typography sx={{ ...SalesAidsStyles.wordStyle, flex: 1 }}>
            Spanish
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Title"
            value={meta.title_en}
            onChange={(e) => handleMetaChange("title_en", e.target.value)}
            size="medium"
            fullWidth
          />
          <TextField
            label="Título"
            value={meta.title_es}
            onChange={(e) => handleMetaChange("title_es", e.target.value)}
            size="medium"
            fullWidth
          />
        </Box>

        <Divider />

        <Typography variant="subtitle2" fontWeight={600}>
          Columns
        </Typography>

        {columns.map((col, index) => (
          <Box
            key={col.id}
            sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
          >
            {index > 0 ? <Divider sx={{ mb: 0.5 }} /> : null}

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                Column {index + 1}
              </Typography>
              {columns.length > 1 ? (
                <IconButton
                  size="small"
                  onClick={() => handleRemoveColumn(col.id)}
                  sx={{ ml: "auto", color: "text.secondary" }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Column Header"
                value={col.column_header_en}
                onChange={(e) =>
                  handleColumnChange(col.id, "column_header_en", e.target.value)
                }
                size="medium"
                fullWidth
              />
              <TextField
                label="Encabezado de Columna"
                value={col.column_header_es}
                onChange={(e) =>
                  handleColumnChange(col.id, "column_header_es", e.target.value)
                }
                size="medium"
                fullWidth
              />
            </Box>

            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Column Items (one per line)"
                value={col.column_items_en}
                onChange={(e) =>
                  handleColumnChange(col.id, "column_items_en", e.target.value)
                }
                size="medium"
                fullWidth
                multiline
                minRows={4}
                helperText="Enter each bullet on its own line"
              />
              <TextField
                label="Artículos de Columna (uno por línea)"
                value={col.column_items_es}
                onChange={(e) =>
                  handleColumnChange(col.id, "column_items_es", e.target.value)
                }
                size="medium"
                fullWidth
                multiline
                minRows={4}
              />
            </Box>
          </Box>
        ))}

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AddCircleOutline
            sx={{ fontSize: 18, color: Theme.palette.primary.main }}
          />
          <AppButton
            variant="text"
            buttonText="Add Column"
            size="small"
            sx={{ textDecoration: "underline" }}
            clickHandler={handleAddColumn}
          />
        </Box>

        <Divider />

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Footer"
            value={meta.footer_en}
            onChange={(e) => handleMetaChange("footer_en", e.target.value)}
            size="medium"
            fullWidth
            multiline
            minRows={2}
          />
          <TextField
            label="Pie de página"
            value={meta.footer_es}
            onChange={(e) => handleMetaChange("footer_es", e.target.value)}
            size="medium"
            fullWidth
            multiline
            minRows={2}
          />
        </Box>
      </Box>
    </DialogContent>
  );
};

export default TimeExclusionPopup;
